import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CitizenshipSelect, { CitizenshipBadges } from "@/components/CitizenshipSelect";

// ---- Phase D: Post-finalize batch processing panel -----------------------
const PROC_TONE = {
  uploaded:                { label: "Queued",           cls: "bg-slate-100 text-slate-500",           icon: "Clock" },
  security_scanning:       { label: "Scanning",         cls: "bg-indigo-50 text-indigo-600",           icon: "ShieldCheck" },
  processing_preview:      { label: "Preview",          cls: "bg-blue-50 text-blue-600",               icon: "Loader2" },
  extracting_text:         { label: "OCR",              cls: "bg-blue-50 text-blue-600",               icon: "Loader2" },
  ready:                   { label: "Ready",            cls: "bg-emerald-50 text-emerald-700",          icon: "CheckCircle2" },
  partially_ready:         { label: "Available",        cls: "bg-emerald-50 text-emerald-600",          icon: "CheckCircle2" },
  processing_failed:       { label: "Failed",           cls: "bg-rose-50 text-rose-600",               icon: "AlertTriangle" },
  quarantined:             { label: "Quarantined",      cls: "bg-rose-50 text-rose-700",               icon: "ShieldAlert" },
  pending_security_review: { label: "Security Hold",   cls: "bg-amber-50 text-amber-700",             icon: "ShieldAlert" },
};
const CLASSIF_TONE = {
  confirmed:    { label: "Classified",         cls: "bg-emerald-50 text-emerald-700" },
  suggested:    { label: "AI Suggested",       cls: "bg-amber-50 text-amber-700" },
  unclassified: { label: "Unclassified",       cls: "bg-slate-100 text-slate-500" },
  null:         { label: "Pending",            cls: "bg-slate-100 text-slate-400" },
};

function ProcBadge({ status }) {
  const tone = PROC_TONE[status] || PROC_TONE["uploaded"];
  const Ic = Icons[tone.icon] || Icons.Clock;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${tone.cls}`} data-testid={`hi-proc-badge-${status}`}>
      <Ic className={`h-3 w-3 ${tone.icon === "Loader2" ? "animate-spin" : ""}`} />{tone.label}
    </span>
  );
}

function ClassifBadge({ status }) {
  const t = CLASSIF_TONE[status] || CLASSIF_TONE["null"];
  return <span className={`inline-flex text-[10px] font-medium px-2 py-0.5 rounded-full ${t.cls}`}>{t.label}</span>;
}

function BatchProcessingPanel({ batchId, onDone }) {
  const [status, setStatus] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/historical-import/drafts/${batchId}/processing-status`);
      setStatus(data);
      if (data.all_terminal) {
        clearInterval(pollRef.current);
      }
    } catch {
      clearInterval(pollRef.current);
    }
  }, [batchId]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const { data } = await api.post(`/historical-import/drafts/${batchId}/reprocess`);
      toast.success(`${data.scheduled} document(s) queued for reprocessing.`);
      fetchStatus();
    } catch { toast.error("Reprocess request failed."); }
    finally { setReprocessing(false); }
  };

  const TERMINAL = ["ready", "partially_ready", "processing_failed", "quarantined", "pending_security_review"];
  const inProgress = status ? (status.queued + status.processing) : null;
  const hasFailures = status ? (status.failed + status.quarantined + status.security_hold) > 0 : false;

  return (
    <div className="space-y-5" data-testid="hi-processing-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Icons.CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <h2 className="font-heading text-lg font-bold text-[#171B2E]">Import Complete — Documents Processing</h2>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">Documents are now entering the canonical processing pipeline. This panel updates automatically.</p>
        </div>
        <button onClick={onDone} data-testid="hi-processing-done"
          className="inline-flex items-center gap-1.5 text-sm font-medium bg-[#1C2431] text-white rounded-lg px-4 py-2 hover:bg-[#2a3050]">
          <Icons.ArrowRight className="h-4 w-4" />Done
        </button>
      </div>

      {/* Summary counts */}
      {status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="hi-processing-summary">
          {[
            ["Total",            status.total,              "text-slate-700",   "bg-slate-50"],
            ["Queued",           status.queued,             "text-slate-500",   "bg-slate-50"],
            ["Processing",       status.processing,         "text-blue-600",    "bg-blue-50"],
            ["Ready",            status.ready,              "text-emerald-700", "bg-emerald-50"],
            ["Available",        status.partially_ready,    "text-emerald-600", "bg-emerald-50"],
            ["Failed",           status.failed,             "text-rose-600",    "bg-rose-50"],
            ["Security Hold",    status.security_hold,      "text-amber-700",   "bg-amber-50"],
            ["Needs Classif.",   status.classification_pending, "text-amber-600","bg-amber-50"],
          ].map(([lbl, val, txtCls, bgCls]) => (
            <div key={lbl} className={`${bgCls} rounded-xl p-3 text-center`} data-testid={`hi-summary-${lbl.toLowerCase().replace(/ /g, "-")}`}>
              <div className={`text-xl font-bold ${txtCls}`}>{val ?? 0}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {status && status.total > 0 && (
        <div data-testid="hi-processing-progress">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-500">{status.all_terminal ? "All documents processed" : `${inProgress} document(s) still processing…`}</span>
            <span className="font-medium text-slate-600">{status.ready + status.partially_ready} / {status.total} ready</span>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${status.total ? Math.round((status.ready + status.partially_ready) / status.total * 100) : 0}%` }} />
          </div>
          {hasFailures && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-rose-600 flex items-center gap-1"><Icons.AlertTriangle className="h-3.5 w-3.5" />{status.failed + status.quarantined + status.security_hold} document(s) need attention.</p>
              <button onClick={reprocess} disabled={reprocessing || !status.all_terminal} data-testid="hi-reprocess-btn"
                className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-md px-3 py-1.5 disabled:opacity-50">
                {reprocessing ? "Reprocessing…" : "Retry Failed"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Per-document grid */}
      {status?.documents?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hi-processing-grid">
          <div className="px-4 py-3 border-b border-slate-100">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Document Status ({status.documents.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5 text-left font-semibold">Document</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Year</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Classification</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Processing</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Intelligence</th>
                  <th className="px-4 py-2.5 text-left font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {status.documents.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50" data-testid={`hi-doc-row-${d.id}`}>
                    <td className="px-4 py-2.5 max-w-[200px]">
                      <div className="truncate text-[#171B2E] font-medium text-xs">{d.display_filename || d.original_filename || d.id}</div>
                      {d.category && <div className="text-[10px] text-slate-400 truncate">{d.category}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{d.tax_year || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {d.classification_label
                        ? <ClassifBadge status={d.classification_status} />
                        : <ClassifBadge status={null} />
                      }
                      {d.classification_label && <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[140px]">{d.classification_label}</div>}
                    </td>
                    <td className="px-4 py-2.5"><ProcBadge status={d.processing_status} /></td>
                    <td className="px-4 py-2.5 text-[10px] text-slate-400">{d.intelligence_status || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {TERMINAL.includes(d.processing_status) && (
                        <a href={`/documents/${d.id}`} target="_blank" rel="noreferrer"
                          data-testid={`hi-open-doc-${d.id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                          <Icons.ExternalLink className="h-3 w-3" />Open
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!status && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Icons.Loader2 className="h-4 w-4 animate-spin" />Loading processing status…</div>
      )}
    </div>
  );
}

const CLASSIFICATIONS = [
  { key: "lead", label: "Lead", icon: "UserPlus", tone: "amber",
    def: "Has contacted the firm but has not yet become a client." },
  { key: "inactive_client", label: "Inactive Client", icon: "UserMinus", tone: "slate",
    def: "An established client with no current open engagement or work in process." },
  { key: "active_client", label: "Active Client", icon: "Users", tone: "emerald",
    def: "An established client with genuine work currently open. (Available in Phase 3.)" },
];
const CERTAINTY = ["exact", "estimated", "unknown"];
const SOURCES = ["SuiteDesk", "Email Inquiry", "Website Contact Form", "Phone", "Referral", "Existing Client Referral",
  "Social Media", "Conference", "Walk-In", "Returning Contact", "Manual Historical Entry", "Other", "Unknown"];
const DIRECTIONS = ["inbound", "outbound"];

// Rich (Tax Profile + Documents) steps are offered for established clients (inactive + active).
const stepsFor = (cls) => {
  const s = [
    { key: "classification", label: "Classification" },
    { key: "match", label: "Match / Create" },
    { key: "contact", label: "Contact" },
    { key: "history", label: "Relationship History" },
  ];
  if (cls === "inactive_client" || cls === "active_client") {
    s.push(
      { key: "taxprofile", label: "Tax Profile" },
      { key: "services",   label: "Historical Services" },
      { key: "documents",  label: "Documents" }
    );
  }
  s.push({ key: "emails", label: "Emails" }, { key: "notes", label: "Notes" });
  if (cls === "active_client") s.push({ key: "engagement", label: "Current Engagement" });
  s.push({ key: "review", label: "Review" });
  return s;
};

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
);
const inp = "w-full mt-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40";
const uid = () => Math.random().toString(36).slice(2, 10);

export default function HistoricalImport() {
  const [cfg, setCfg] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [did, setDid] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState([]);
  const [tpSections, setTpSections] = useState([]);
  const [tpPick, setTpPick] = useState("");
  const [tpVal, setTpVal] = useState("");
  const [tpVerified, setTpVerified] = useState(false);
  const [review, setReview] = useState(null);
  const [engList, setEngList] = useState([]);
  const [svcCatalog, setSvcCatalog] = useState([]);
  // Phase 1: historical service entries local state (synced to draft.historical_services)
  const [histSvcs, setHistSvcs] = useState([]);
  const [svcSaving, setSvcSaving] = useState(false);
  // Phase D: post-finalize processing panel
  const [processingBatchId, setProcessingBatchId] = useState(null);
  // Phase 2: post-finalize completion + portal invitation panel
  const [finalizedResult, setFinalizedResult] = useState(null); // {clientId, classification, svcCount, emailCount, clientName, hasEmail}
  const [portalInviteStatus, setPortalInviteStatus] = useState(null); // null | 'sending' | 'sent' | 'already_active' | 'no_email' | 'error'
  const [czPaste, setCzPaste] = useState("");
  const [czPreview, setCzPreview] = useState(null);
  const fileRef = useRef(null);

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get("/historical-import/config"); setCfg(data); }
    catch (e) { setCfg({ enabled: false, can_manage: false }); }
  }, []);
  const loadDrafts = useCallback(async () => {
    try { const { data } = await api.get("/historical-import/drafts"); setDrafts(data.drafts || []); }
    catch (e) { console.warn("drafts load failed", e); }
  }, []);
  useEffect(() => { loadCfg(); loadDrafts(); }, [loadCfg, loadDrafts]);

  const steps = stepsFor(draft?.classification);
  const stepKey = steps[stepIdx]?.key;

  const refreshDraft = async (id = did) => {
    const { data } = await api.get(`/historical-import/drafts/${id}`);
    setDraft(data); return data;
  };
  const openDraft = async (id) => {
    const { data } = await api.get(`/historical-import/drafts/${id}`);
    setDraft(data); setDid(id); setStepIdx(data.classification ? 1 : 0);
  };
  const startNew = async (classification) => {
    const { data } = await api.post("/historical-import/drafts", { classification });
    setDraft(data); setDid(data.id); setStepIdx(1); loadDrafts();
  };
  const save = async (patch) => {
    const { data } = await api.patch(`/historical-import/drafts/${did}`, patch);
    setDraft(data); return data;
  };
  const exitWizard = () => { setDid(null); setDraft(null); setStepIdx(0); setReview(null); setMatches([]); loadDrafts(); };

  const setCitizenships = (entries) => save({ contact: { ...draft.contact, citizenships: entries } });
  const parseCitizenship = async () => {
    if (!czPaste.trim()) return;
    try {
      const { data } = await api.post("/historical-import/citizenship/parse", { value: czPaste });
      setCzPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not parse value"); }
  };
  const applyCitizenshipPreview = async () => {
    if (!czPreview) return;
    await save({ contact: { ...draft.contact, citizenships: czPreview.entries || [], citizenship_source_value: czPreview.source_value } });
    if ((czPreview.entries || []).length === 0) toast.warning("Ambiguous value — select the actual countries manually above.");
    else toast.success("Citizenships applied.");
    setCzPreview(null); setCzPaste("");
  };

  const runDedupe = async () => {
    const c = draft.contact || {};
    const { data } = await api.post("/historical-import/dedupe", {
      classification: draft.classification, email: c.email, full_name: [c.first_name, c.last_name].filter(Boolean).join(" "),
      business_name: c.business_name, phone: c.phone });
    setMatches(data.matches || []);
  };

  // ---- Tax Profile ----
  useEffect(() => {
    if (stepKey === "taxprofile" && tpSections.length === 0) {
      api.get("/historical-import/tax-profile-fields").then(({ data }) => setTpSections(data.sections || [])).catch(() => {});
    }
  }, [stepKey, tpSections.length]);
  const fieldMeta = (key) => { for (const s of tpSections) { const f = (s.fields || []).find((x) => x.key === key); if (f) return f; } return null; };
  const addFact = async () => {
    if (!tpPick || tpVal === "") return;
    const facts = [...((draft.tax_profile?.facts) || []).filter((f) => f.field_key !== tpPick),
      { field_key: tpPick, value: tpVal, verified: tpVerified }];
    await save({ tax_profile: { facts } });
    setTpPick(""); setTpVal(""); setTpVerified(false);
  };
  const removeFact = async (key) => {
    const facts = ((draft.tax_profile?.facts) || []).filter((f) => f.field_key !== key);
    await save({ tax_profile: { facts } });
  };

  // ---- Documents ----
  const uploadDoc = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/historical-import/drafts/${did}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await refreshDraft(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const editDocMeta = (entryId, patch) => {
    const documents = (draft.documents || []).map((d) => (d.id === entryId ? { ...d, ...patch } : d));
    setDraft({ ...draft, documents });
  };
  const saveDocs = () => save({ documents: draft.documents });
  const deleteDoc = async (entryId) => { await api.delete(`/historical-import/drafts/${did}/documents/${entryId}`); await refreshDraft(); };

  // ---- Emails ----
  const addEmail = () => {
    const emails = [...(draft.emails || []), { _uid: uid(), thread_key: "", subject: "", direction: "inbound",
      from: "", to: "", cc: "", body: "", sent_at: "", tax_year: "", source_system: "", attachments: [] }];
    setDraft({ ...draft, emails });
  };
  const editEmail = (i, patch) => {
    const emails = draft.emails.map((e, idx) => (idx === i ? { ...e, ...patch } : e)); setDraft({ ...draft, emails });
  };
  const removeEmail = (i) => { const emails = draft.emails.filter((_, idx) => idx !== i); setDraft({ ...draft, emails }); save({ emails }); };
  const saveEmails = () => {
    // normalize to/cc string -> array for API
    const emails = (draft.emails || []).map((e) => ({ ...e,
      to: Array.isArray(e.to) ? e.to : String(e.to || "").split(",").map((x) => x.trim()).filter(Boolean),
      cc: Array.isArray(e.cc) ? e.cc : String(e.cc || "").split(",").map((x) => x.trim()).filter(Boolean) }));
    return save({ emails });
  };

  // ---- Review ----
  const loadReview = useCallback(async () => {
    try { const { data } = await api.get(`/historical-import/drafts/${did}/review`); setReview(data); }
    catch (e) { console.warn("review load failed", e); }
  }, [did]);
  useEffect(() => { if (stepKey === "review" && did) loadReview(); }, [stepKey, did, loadReview]);

  // ---- Historical Services ----
  // Sync from draft when entering the services step
  useEffect(() => {
    if (stepKey === "services" && draft) {
      setHistSvcs(draft.historical_services || []);
      if (svcCatalog.length === 0)
        api.get("/historical-import/service-catalog").then(({ data }) => setSvcCatalog(data.services || [])).catch(() => {});
    }
  }, [stepKey, draft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addHistSvc = () => {
    setHistSvcs((prev) => [...prev, {
      _uid: uid(), service_name: "", service_code: null, catalog_id: null,
      tax_year: "", service_period: "", completed_date: "",
      amount_paid: "", amount_charged: "", currency: "USD",
      description: "", internal_note: "", source_reference: "",
    }]);
  };
  const editHistSvc = (i, patch) => setHistSvcs((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeHistSvc = (i) => setHistSvcs((prev) => prev.filter((_, idx) => idx !== i));
  const saveHistSvcs = async () => {
    setSvcSaving(true);
    try {
      await save({ historical_services: histSvcs });
      toast.success("Historical services saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSvcSaving(false); }
  };

  // ---- Active Engagement ----
  useEffect(() => {
    if (stepKey === "engagement" && did) {
      api.get(`/historical-import/drafts/${did}/engagements`).then(({ data }) => setEngList(data.engagements || [])).catch(() => {});
      if (svcCatalog.length === 0) api.get("/historical-import/service-catalog").then(({ data }) => setSvcCatalog(data.services || [])).catch(() => {});
    }
  }, [stepKey, did, svcCatalog.length]);
  const ae = draft?.active_engagement || {};
  const setAE = (patch) => { const next = { ...(draft.active_engagement || {}), ...patch }; setDraft({ ...draft, active_engagement: next }); return save({ active_engagement: next }); };
  const addSvcLine = () => { const services = [...((ae.services) || []), { name: "", amount: 0 }]; setAE({ services }); };
  const editSvc = (i, patch) => { const services = (ae.services || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)); setDraft({ ...draft, active_engagement: { ...ae, services } }); };
  const reclassifyInactive = async () => { await save({ classification: "inactive_client" }); setStepIdx(0); toast.info("Reclassified as Inactive Client. Your draft is preserved."); };
  const setConflictDecision = async (fieldKey, decision) => {
    const facts = ((draft.tax_profile?.facts) || []).map((f) => (f.field_key === fieldKey ? { ...f, decision } : f));
    await save({ tax_profile: { facts } });
    await loadReview();
  };

  // Phase 2: invite finalized historical client to portal
  const inviteToPortal = async () => {
    if (!window.confirm(`This will send a portal invitation email to ${finalizedResult.email || "the client"}.\n\nThe client will receive a "Welcome to Your Secure Client Portal" email with a secure activation link.\n\nContinue?`)) return;
    setPortalInviteStatus("sending");
    try {
      const { data } = await api.post(`/portal/admin/${finalizedResult.clientId}/enable`);
      setPortalInviteStatus("sent");
      toast.success(`Portal invitation sent to ${data.email || "client"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && typeof detail === "string" && detail.toLowerCase().includes("email")) {
        setPortalInviteStatus("no_email");
      } else {
        setPortalInviteStatus("error");
        toast.error(typeof detail === "string" ? detail : "Failed to send portal invitation");
      }
    }
  };

  const exitAfterFinalize = () => { setFinalizedResult(null); setPortalInviteStatus(null); exitWizard(); };

  const finalize = async (force = false) => {
    setBusy(true);
    try {
      if (force) await save({ force: true });
      const { data } = await api.post(`/historical-import/drafts/${did}/finalize`, {});
      const svcCount = data.service_records_imported ?? 0;
      const svcMsg = svcCount > 0 ? `, ${svcCount} service record(s)` : "";
      // Phase D: show processing panel after successful finalize (with documents)
      if (data.documents_imported > 0) {
        toast.success(`Imported ${data.classification.replace("_", " ")} — ${data.documents_imported} doc(s) entering pipeline${svcMsg}, ${data.email_messages || 0} email(s)`);
        setProcessingBatchId(did);
      } else {
        // Phase 2: show completion panel with portal invitation option
        const clientName = [draft.contact?.first_name, draft.contact?.last_name].filter(Boolean).join(" ") ||
                           draft.contact?.business_name || "(unnamed)";
        toast.success(`Imported ${data.classification.replace("_", " ")}${svcMsg}, ${data.email_messages || 0} email(s)`);
        setFinalizedResult({
          clientId: data.client_id,
          classification: data.classification,
          svcCount,
          emailCount: data.email_messages || 0,
          clientName,
          hasEmail: !!(draft.contact?.email),
          email: draft.contact?.email || null,
        });
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.duplicates) {
        if (window.confirm(`${det.message}\n\n${det.duplicates.map((m) => `• ${m.full_name} (${m.email || ""})`).join("\n")}\n\nCreate anyway?`)) return finalize(true);
      } else if (e?.response?.status === 409 && det?.conflicts) {
        toast.error("Resolve tax profile conflicts before finalizing."); await loadReview();
      } else if (e?.response?.status === 400 && det?.block === "active_engagement_required") {
        toast.error("Active Client needs a qualifying engagement — select one, create one, or reclassify as Inactive.");
      } else { toast.error(typeof det === "string" ? det : det?.message || "Finalize failed"); }
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!cfg.enabled || !cfg.can_manage) return (
    <div className="max-w-lg mx-auto mt-20 text-center" data-testid="hi-disabled">
      <Icons.Lock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <h1 className="font-heading text-xl font-bold text-slate-700">Historical Import Unavailable</h1>
      <p className="text-sm text-slate-500 mt-2">{!cfg.enabled ? "This tool is currently disabled by configuration." : "You don't have permission to use this tool."}</p>
    </div>
  );

  // Phase 2: show completion panel with portal invitation option
  if (finalizedResult) {
    const { clientId, classification, svcCount, emailCount, clientName, hasEmail, email } = finalizedResult;
    const classLabel = (classification || "").replace(/_/g, " ");
    return (
      <div className="max-w-4xl mx-auto p-6" data-testid="hi-completion-panel">
        <div className="flex items-center gap-2 mb-6">
          <Icons.DatabaseBackup className="h-5 w-5 text-[#F5A623]" />
          <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Historical Import</h1>
          <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Import Complete</span>
        </div>

        {/* Import summary */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 mb-6" data-testid="hi-import-summary">
          <div className="flex items-start gap-3">
            <Icons.CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-emerald-800 mb-1">Import Complete</h2>
              <p className="text-sm text-emerald-700"><span className="font-medium">{clientName}</span> has been imported as a {classLabel}.</p>
              {svcCount > 0 && <p className="text-sm text-emerald-700 mt-0.5">{svcCount} historical service record{svcCount !== 1 ? "s" : ""} recorded as completed and paid.</p>}
              {emailCount > 0 && <p className="text-sm text-emerald-700 mt-0.5">{emailCount} email message{emailCount !== 1 ? "s" : ""} imported.</p>}
              <p className="text-xs text-emerald-600 mt-2">No workflow, invoice, portal invitation, or client communication was created by this import.</p>
            </div>
          </div>
        </div>

        {/* Portal invitation section — explicit staff-controlled action */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="hi-portal-invite-section">
          <h2 className="font-semibold text-[#171B2E] mb-1 flex items-center gap-2"><Icons.Mail className="h-4 w-4 text-[#F5A623]" /> Invite to Secure Portal</h2>
          <p className="text-sm text-slate-500 mb-4">
            Once you have verified the imported record, you can invite this client to their Secure Portal.
            This action sends an email — the client will receive a portal activation link. 
            The invitation is separate from this import and is entirely under your control.
          </p>

          {!hasEmail && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="hi-invite-no-email">
              This client has no email address on file. Add an email in CRM before sending a portal invitation.
            </div>
          )}

          {hasEmail && portalInviteStatus === null && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-slate-400">Invitation will be sent to: <span className="font-medium text-slate-600">{email}</span></p>
              <button
                data-testid="hi-invite-portal-btn"
                onClick={inviteToPortal}
                className="self-start flex items-center gap-2 px-4 py-2 bg-[#171B2E] hover:bg-[#232842] text-white font-medium rounded-lg text-sm transition-colors"
              >
                <Icons.Mail className="h-4 w-4" /> Invite to Secure Portal
              </button>
            </div>
          )}

          {hasEmail && portalInviteStatus === "sending" && (
            <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="hi-invite-sending">
              <Icons.Loader2 className="h-4 w-4 animate-spin" /> Sending invitation…
            </div>
          )}

          {portalInviteStatus === "sent" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="hi-invite-status">
              <Icons.CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>Portal invitation sent to <span className="font-medium">{email}</span>. The client will receive an activation link by email.</span>
            </div>
          )}

          {portalInviteStatus === "error" && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="hi-invite-error">
              Failed to send portal invitation. You can retry from the client's CRM record.
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            data-testid="hi-completion-done"
            onClick={exitAfterFinalize}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
          >
            Return to Historical Import
          </button>
          <a
            href={`/client-workspace/${clientId}`}
            data-testid="hi-completion-view-client"
            className="px-4 py-2 bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-medium rounded-lg text-sm flex items-center gap-1.5"
          >
            <Icons.ExternalLink className="h-3.5 w-3.5" /> View Client Record
          </a>
        </div>
      </div>
    );
  }

  // Phase D: show batch processing panel after successful finalize (with documents)
  if (processingBatchId) {
    return (
      <div className="max-w-4xl mx-auto p-6" data-testid="hi-processing-view">
        <div className="flex items-center gap-2 mb-6">
          <Icons.DatabaseBackup className="h-5 w-5 text-[#F5A623]" />
          <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Historical Import</h1>
          <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Finalized</span>
        </div>
        <BatchProcessingPanel
          batchId={processingBatchId}
          onDone={() => { setProcessingBatchId(null); exitWizard(); }}
        />
      </div>
    );
  }

  const classification = draft?.classification;
  const activeBlocked = classification === "active_client";
  const facts = (draft?.tax_profile?.facts) || [];

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="historical-import">
      <div className="flex items-center gap-2 mb-1">
        <Icons.DatabaseBackup className="h-5 w-5 text-[#F5A623]" />
        <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Historical Import</h1>
        <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Phase {cfg.phase || 3} · Admin only</span>
      </div>
      <p className="text-sm text-slate-500 mb-6">Reconstruct a historical Lead or Client with tax profile, documents and emails. Nothing is created or sent until you Finalize.</p>

      {!did ? (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-3">
            {CLASSIFICATIONS.map((c) => (
              <button key={c.key} onClick={() => startNew(c.key)} data-testid={`hi-start-${c.key}`}
                className="text-left border border-slate-200 rounded-xl p-4 hover:border-[#F5A623] hover:shadow-sm transition bg-white">
                <div className="flex items-center gap-2 mb-1">{(() => { const I = Icons[c.icon]; return <I className="h-4 w-4 text-[#171B2E]" />; })()}<span className="font-semibold text-sm text-[#171B2E]">{c.label}</span></div>
                <p className="text-xs text-slate-500">{c.def}</p>
              </button>
            ))}
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-600 mb-2">Saved drafts ({drafts.length})</h2>
            <div className="space-y-2" data-testid="hi-drafts">
              {drafts.length === 0 && <p className="text-xs text-slate-400">No in-progress imports.</p>}
              {drafts.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 bg-white">
                  <div className="text-sm"><span className="font-medium">{[d.contact?.first_name, d.contact?.last_name].filter(Boolean).join(" ") || d.contact?.business_name || "(unnamed)"}</span>
                    <span className="ml-2 text-[10px] uppercase bg-slate-100 text-slate-500 px-1.5 rounded-full">{d.classification?.replace("_", " ")}</span></div>
                  <div className="flex gap-2">
                    <button onClick={() => openDraft(d.id)} data-testid={`hi-resume-${d.id}`} className="text-xs text-blue-600 hover:underline">Resume</button>
                    <button onClick={async () => { await api.delete(`/historical-import/drafts/${d.id}`); loadDrafts(); }} className="text-xs text-rose-500 hover:underline">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1 mb-6 text-[11px]" data-testid="hi-stepper">
            {steps.map((s, i) => (
              <div key={s.key} className={`flex items-center gap-1 ${i === stepIdx ? "text-[#171B2E] font-semibold" : "text-slate-400"}`}>
                <span className={`h-5 w-5 rounded-full flex items-center justify-center border ${i < stepIdx ? "bg-emerald-500 border-emerald-500 text-white" : i === stepIdx ? "bg-[#F5A623] border-[#F5A623] text-[#171B2E]" : "border-slate-300"}`}>{i < stepIdx ? "✓" : i + 1}</span>
                <span className="hidden sm:inline">{s.label}</span>{i < steps.length - 1 && <span className="mx-1 text-slate-300">›</span>}
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 min-h-[280px]">
            {stepKey === "classification" && (
              <div><h3 className="font-semibold mb-3">Relationship classification</h3>
                {CLASSIFICATIONS.map((c) => (
                  <label key={c.key} className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="radio" name="cls" checked={classification === c.key} onChange={() => save({ classification: c.key })} className="mt-1 accent-[#F5A623]" />
                    <span><span className="font-medium text-sm">{c.label}</span><br /><span className="text-xs text-slate-500">{c.def}</span></span>
                  </label>
                ))}
              </div>
            )}

            {stepKey === "match" && (
              <div><h3 className="font-semibold mb-3">Match existing or create new</h3>
                <div className="flex gap-2 mb-4">
                  {["create", "match"].map((m) => (
                    <button key={m} onClick={() => save({ mode: m })} data-testid={`hi-mode-${m}`}
                      className={`px-3 py-1.5 rounded-lg text-sm border ${draft.mode === m ? "bg-[#171B2E] text-white border-[#171B2E]" : "border-slate-300 text-slate-600"}`}>{m === "create" ? "Create New" : "Match Existing"}</button>
                  ))}
                </div>
                <button onClick={runDedupe} className="text-xs text-blue-600 hover:underline mb-2" data-testid="hi-dedupe-btn">Search for possible duplicates →</button>
                {matches.length > 0 && (
                  <div className="space-y-1" data-testid="hi-matches">
                    {matches.map((m) => (
                      <div key={m.id} className={`text-xs border rounded px-2 py-1.5 flex items-center justify-between ${m.conflict ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}>
                        <span>{m.full_name} · <span className="text-slate-400">{m.email}</span> · <span className="uppercase">{m.match_type}</span>{m.conflict && <span className="text-rose-600 font-medium"> — already a Client (cannot import as Lead)</span>}</span>
                        {draft.mode === "match" && !m.conflict && <button onClick={() => save({ matched_record_id: m.id })} className={`text-blue-600 ${draft.matched_record_id === m.id ? "font-bold" : ""}`}>{draft.matched_record_id === m.id ? "Selected" : "Select"}</button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {stepKey === "contact" && (
              <div><h3 className="font-semibold mb-3">Contact information</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[["first_name", "First name"], ["last_name", "Last name"], ["business_name", "Business name"], ["email", "Primary email"], ["secondary_email", "Alternate email"], ["phone", "Primary phone"], ["country_of_residence", "Country of residence"], ["spouse_name", "Spouse / related"], ["referral_source", "Referral source"]].map(([k, l]) => (
                    <Field key={k} label={l}><input className={inp} value={draft.contact?.[k] || ""} onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, [k]: e.target.value } })} onBlur={() => save({ contact: draft.contact })} data-testid={`hi-contact-${k}`} /></Field>
                  ))}
                  <Field label="SuiteDesk legacy ID"><input className={inp} value={draft.legacy_identifiers?.suitedesk_id || ""} onChange={(e) => setDraft({ ...draft, legacy_identifiers: { ...draft.legacy_identifiers, suitedesk_id: e.target.value } })} onBlur={() => save({ legacy_identifiers: draft.legacy_identifiers })} /></Field>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">Citizenship(s)</span>
                  <div className="mt-1"><CitizenshipSelect value={draft.contact?.citizenships || []} onChange={setCitizenships} testidPrefix="hi-citizenship" /></div>
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <span className="text-[11px] font-semibold text-slate-500">Paste legacy value</span>
                    <div className="flex gap-2 mt-1">
                      <input className={inp} placeholder='e.g. "Dual US-Thai", "American / Canadian"' value={czPaste} onChange={(e) => setCzPaste(e.target.value)} data-testid="hi-citizenship-paste" />
                      <button onClick={parseCitizenship} className="shrink-0 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg px-3 hover:bg-blue-50" data-testid="hi-citizenship-parse">Normalize</button>
                    </div>
                    {czPreview && (
                      <div className="mt-2 text-xs" data-testid="hi-citizenship-preview">
                        <div className="text-slate-500">Source: <span className="font-mono">{czPreview.source_value}</span></div>
                        {(czPreview.entries || []).length > 0 ? (
                          <div className="mt-1 flex items-center gap-2">Normalized: <CitizenshipBadges value={czPreview.entries} testid="hi-citizenship-preview-badges" /></div>
                        ) : (
                          <div className="mt-1 flex items-center gap-1 text-rose-600 font-medium" data-testid="hi-citizenship-review-warning"><Icons.AlertTriangle className="h-3.5 w-3.5" /> Citizenship details required — no countries identified. Select them manually above before finalizing.</div>
                        )}
                        {(czPreview.warnings || []).map((w, i) => (<div key={i} className="mt-0.5 flex items-center gap-1 text-amber-600"><Icons.AlertTriangle className="h-3 w-3" /> {w.message}</div>))}
                        {(czPreview.entries || []).length > 0 && <button onClick={applyCitizenshipPreview} className="mt-1.5 text-xs font-medium text-emerald-700 border border-emerald-300 rounded-lg px-3 py-1 hover:bg-emerald-50" data-testid="hi-citizenship-apply">Use these countries</button>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {stepKey === "history" && (
              <div><h3 className="font-semibold mb-3">Relationship history</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Date of first contact"><input type="date" className={inp} value={(draft.relationship_history?.first_contact_date || "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, relationship_history: { ...draft.relationship_history, first_contact_date: e.target.value ? e.target.value + "T00:00:00Z" : null } })} onBlur={() => save({ relationship_history: draft.relationship_history })} data-testid="hi-first-contact-date" /></Field>
                  <Field label="Date certainty"><select className={inp} value={draft.relationship_history?.first_contact_certainty || "unknown"} onChange={(e) => save({ relationship_history: { ...draft.relationship_history, first_contact_certainty: e.target.value } }).then(setDraft)} data-testid="hi-first-contact-certainty">{CERTAINTY.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
                  <Field label="First contact source"><select className={inp} value={draft.relationship_history?.first_contact_source || ""} onChange={(e) => save({ relationship_history: { ...draft.relationship_history, first_contact_source: e.target.value } }).then(setDraft)}><option value="">—</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                  {classification === "inactive_client" && (
                    <Field label="Date last work completed"><input type="date" className={inp} value={(draft.relationship_history?.date_last_work_completed || "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, relationship_history: { ...draft.relationship_history, date_last_work_completed: e.target.value ? e.target.value + "T00:00:00Z" : null, date_last_work_completed_certainty: "estimated" } })} onBlur={() => save({ relationship_history: draft.relationship_history })} /></Field>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-3">Set certainty to <b>Unknown</b> if you don't know the date — it will never be auto-filled with today's date.</p>
              </div>
            )}

            {stepKey === "taxprofile" && (
              <div data-testid="hi-step-taxprofile"><h3 className="font-semibold mb-1">Tax Profile facts</h3>
                <p className="text-[11px] text-slate-400 mb-3">Imported facts are marked <b>Provided</b> (source: imported). Tick <b>Verified</b> only if validated against reliable source documentation. Conflicts with existing values are resolved at Review.</p>
                <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end mb-3">
                  <Field label="Field"><select className={inp} value={tpPick} onChange={(e) => { setTpPick(e.target.value); setTpVal(""); }} data-testid="hi-tp-field">
                    <option value="">Select a field…</option>
                    {tpSections.map((s) => <optgroup key={s.key} label={s.title}>{s.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</optgroup>)}
                  </select></Field>
                  <Field label="Value">{(() => { const fm = fieldMeta(tpPick); return fm && (fm.options || []).length ? (
                    <select className={inp} value={tpVal} onChange={(e) => setTpVal(e.target.value)} data-testid="hi-tp-value"><option value="">—</option>{fm.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                  ) : (<input className={inp} value={tpVal} onChange={(e) => setTpVal(e.target.value)} data-testid="hi-tp-value" />); })()}</Field>
                  <button onClick={addFact} data-testid="hi-tp-add" className="px-3 py-2 bg-[#171B2E] text-white rounded-lg text-sm">Add</button>
                </div>
                <label className="flex items-center gap-1 text-xs text-slate-600 mb-3"><input type="checkbox" checked={tpVerified} onChange={(e) => setTpVerified(e.target.checked)} className="accent-[#F5A623]" data-testid="hi-tp-verified" /> Mark as staff-verified (validated against source documentation)</label>
                <div className="space-y-1" data-testid="hi-tp-list">
                  {facts.length === 0 && <p className="text-xs text-slate-400">No tax profile facts added.</p>}
                  {facts.map((f) => (
                    <div key={f.field_key} className="flex items-center justify-between text-sm border border-slate-200 rounded px-2 py-1.5">
                      <span><span className="font-medium">{(fieldMeta(f.field_key)?.label) || f.field_key}</span>: {String(f.value)} {f.verified && <span className="ml-1 text-[10px] uppercase bg-emerald-100 text-emerald-700 px-1 rounded">verified</span>}</span>
                      <button onClick={() => removeFact(f.field_key)} className="text-xs text-rose-500 hover:underline">Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Historical Services Step ─────────────────────────────── */}
            {stepKey === "services" && (
              <div data-testid="hi-step-services">
                <h3 className="font-semibold mb-1">Historical services</h3>
                <p className="text-[11px] text-slate-400 mb-4">
                  Record services the firm completed for this client and what was paid.
                  These are <strong>historical facts only</strong> — no invoice, payment, or billing record will be created.
                </p>
                <div className="space-y-3" data-testid="hi-svc-list">
                  {histSvcs.length === 0 && (
                    <p className="text-xs text-slate-400 py-2">No historical services added. Use the button below to record a completed service.</p>
                  )}
                  {histSvcs.map((svc, i) => (
                    <div key={svc._uid || i} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/60" data-testid={`hi-svc-entry-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-600">Service {i + 1}</span>
                        <button onClick={() => removeHistSvc(i)} className="text-xs text-rose-500 hover:underline" data-testid={`hi-svc-remove-${i}`}>Remove</button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Field label="Service *">
                          <select className={inp} value={svc.catalog_id || ""} onChange={(e) => {
                            const cat = svcCatalog.find((c) => c.catalog_id === e.target.value);
                            editHistSvc(i, {
                              catalog_id:   cat ? cat.catalog_id : null,
                              service_code: null,
                              service_name: cat ? cat.name : (svc.service_name || ""),
                            });
                          }} data-testid={`hi-svc-catalog-${i}`}>
                            <option value="">— Manual description —</option>
                            {svcCatalog.map((c) => <option key={c.catalog_id} value={c.catalog_id}>{c.name}</option>)}
                          </select>
                          {!svc.catalog_id && (
                            <input className={`${inp} mt-1`} placeholder="Service description (required)" value={svc.service_name || ""} onChange={(e) => editHistSvc(i, { service_name: e.target.value })} data-testid={`hi-svc-name-${i}`} />
                          )}
                        </Field>
                        <Field label="Tax year / period">
                          <input className={inp} placeholder="e.g. 2024" value={svc.tax_year || ""} onChange={(e) => editHistSvc(i, { tax_year: e.target.value })} data-testid={`hi-svc-taxyear-${i}`} />
                        </Field>
                        <Field label="Amount paid *">
                          <input className={inp} type="number" min="0" step="0.01" placeholder="0.00" value={svc.amount_paid ?? ""} onChange={(e) => editHistSvc(i, { amount_paid: e.target.value })} data-testid={`hi-svc-amtpaid-${i}`} />
                        </Field>
                        <Field label="Currency">
                          <input className={inp} maxLength={3} placeholder="USD" value={svc.currency || "USD"} onChange={(e) => editHistSvc(i, { currency: e.target.value.toUpperCase() })} data-testid={`hi-svc-currency-${i}`} />
                        </Field>
                        <Field label="Amount charged (optional)">
                          <input className={inp} type="number" min="0" step="0.01" placeholder="—" value={svc.amount_charged ?? ""} onChange={(e) => editHistSvc(i, { amount_charged: e.target.value === "" ? "" : e.target.value })} data-testid={`hi-svc-amtcharged-${i}`} />
                        </Field>
                        <Field label="Completion date (optional)">
                          <input className={inp} type="date" value={(svc.completed_date || "").slice(0, 10)} onChange={(e) => editHistSvc(i, { completed_date: e.target.value || null })} data-testid={`hi-svc-completed-${i}`} />
                        </Field>
                        <Field label="Service period (if not a tax year)">
                          <input className={inp} placeholder="e.g. Jan–Dec 2024" value={svc.service_period || ""} onChange={(e) => editHistSvc(i, { service_period: e.target.value })} data-testid={`hi-svc-period-${i}`} />
                        </Field>
                        <Field label="Source reference (optional)">
                          <input className={inp} placeholder="Invoice #, legacy ID…" value={svc.source_reference || ""} onChange={(e) => editHistSvc(i, { source_reference: e.target.value })} data-testid={`hi-svc-srcref-${i}`} />
                        </Field>
                      </div>
                      <Field label="Description (optional)">
                        <textarea className={`${inp} resize-none`} rows={2} placeholder="Optional staff description or notes" value={svc.description || ""} onChange={(e) => editHistSvc(i, { description: e.target.value })} data-testid={`hi-svc-desc-${i}`} />
                      </Field>
                      <Field label="Internal note (optional)">
                        <input className={inp} placeholder="Internal staff note — not visible to client" value={svc.internal_note || ""} onChange={(e) => editHistSvc(i, { internal_note: e.target.value })} data-testid={`hi-svc-note-${i}`} />
                      </Field>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button onClick={addHistSvc} className="text-sm text-[#F5A623] font-medium hover:underline flex items-center gap-1" data-testid="hi-svc-add">
                    <Icons.Plus className="h-4 w-4" /> Add Historical Service
                  </button>
                  <button onClick={saveHistSvcs} disabled={svcSaving} className="ml-auto px-4 py-2 bg-[#171B2E] text-white rounded-lg text-sm disabled:opacity-50" data-testid="hi-svc-save">
                    {svcSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}

            {stepKey === "documents" && (
              <div data-testid="hi-step-documents">
                <h3 className="font-semibold mb-1">Documents</h3>
                <p className="text-[11px] text-slate-400 mb-3">Files are held privately in draft storage and only enter the repository on Finalize — never in review queues, matching, or search before then.</p>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f); e.target.value = ""; }} />
                <button onClick={() => fileRef.current?.click()} data-testid="hi-doc-upload" className="mb-3 px-3 py-1.5 bg-[#F5A623] text-[#171B2E] font-medium rounded-lg text-sm flex items-center gap-1"><Icons.Upload className="h-4 w-4" /> Upload document</button>
                <div className="space-y-2" data-testid="hi-doc-list">
                  {(draft.documents || []).length === 0 && <p className="text-xs text-slate-400">No documents added.</p>}
                  {(draft.documents || []).map((d) => (
                    <div key={d.id} className="border border-slate-200 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium flex items-center gap-1"><Icons.FileText className="h-3.5 w-3.5 text-slate-400" />{d.original_filename}</span>
                        <button onClick={() => deleteDoc(d.id)} className="text-xs text-rose-500 hover:underline">Remove</button></div>
                      <div className="grid sm:grid-cols-4 gap-2">
                        <input className={inp} placeholder="Document type" value={d.document_type || ""} onChange={(e) => editDocMeta(d.id, { document_type: e.target.value })} onBlur={saveDocs} data-testid={`hi-doc-type-${d.id}`} />
                        <input className={inp} placeholder="Tax year" value={d.tax_year || ""} onChange={(e) => editDocMeta(d.id, { tax_year: e.target.value })} onBlur={saveDocs} />
                        <input type="date" className={inp} value={(d.document_date || "").slice(0, 10)} onChange={(e) => editDocMeta(d.id, { document_date: e.target.value ? e.target.value + "T00:00:00Z" : null })} onBlur={saveDocs} />
                        <input className={inp} placeholder="Source system" value={d.source_system || ""} onChange={(e) => editDocMeta(d.id, { source_system: e.target.value })} onBlur={saveDocs} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stepKey === "emails" && (
              <div data-testid="hi-step-emails"><h3 className="font-semibold mb-1">Historical emails</h3>
                <p className="text-[11px] text-slate-400 mb-3">Emails are reconstructed as closed, archived history — no sends, no unread badges, no response timers. Group a conversation by giving its messages the same <b>Thread key</b>.</p>
                <button onClick={addEmail} data-testid="hi-email-add" className="mb-3 text-xs text-blue-600 hover:underline">+ Add email</button>
                {(draft.emails || []).length === 0 && <p className="text-xs text-slate-400">No emails added.</p>}
                {(draft.emails || []).map((e, i) => (
                  <div key={e._uid || i} className="border border-slate-200 rounded-lg p-2 mb-2 space-y-2" data-testid={`hi-email-${i}`}>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <input className={inp} placeholder="Thread key" value={e.thread_key || ""} onChange={(ev) => editEmail(i, { thread_key: ev.target.value })} onBlur={saveEmails} />
                      <select className={inp} value={e.direction || "inbound"} onChange={(ev) => editEmail(i, { direction: ev.target.value })} onBlur={saveEmails}>{DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
                      <input type="datetime-local" className={inp} value={(e.sent_at || "").slice(0, 16)} onChange={(ev) => editEmail(i, { sent_at: ev.target.value ? ev.target.value + ":00Z" : "" })} onBlur={saveEmails} />
                      <input className={inp} placeholder="Tax year" value={e.tax_year || ""} onChange={(ev) => editEmail(i, { tax_year: ev.target.value })} onBlur={saveEmails} />
                    </div>
                    <input className={inp} placeholder="Subject" value={e.subject || ""} onChange={(ev) => editEmail(i, { subject: ev.target.value })} onBlur={saveEmails} />
                    <div className="grid sm:grid-cols-3 gap-2">
                      <input className={inp} placeholder="From" value={e.from || ""} onChange={(ev) => editEmail(i, { from: ev.target.value })} onBlur={saveEmails} />
                      <input className={inp} placeholder="To (comma separated)" value={Array.isArray(e.to) ? e.to.join(", ") : (e.to || "")} onChange={(ev) => editEmail(i, { to: ev.target.value })} onBlur={saveEmails} />
                      <input className={inp} placeholder="CC (comma separated)" value={Array.isArray(e.cc) ? e.cc.join(", ") : (e.cc || "")} onChange={(ev) => editEmail(i, { cc: ev.target.value })} onBlur={saveEmails} />
                    </div>
                    <textarea className={inp} placeholder="Message body" value={e.body || ""} onChange={(ev) => editEmail(i, { body: ev.target.value })} onBlur={saveEmails} />
                    {classification === "inactive_client" && (draft.documents || []).length > 0 && (
                      <div className="text-xs">
                        <span className="text-slate-400">Attachments (link imported documents):</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {(draft.documents || []).map((d) => {
                            const on = (e.attachments || []).some((a) => a.document_ref === d.id);
                            return <label key={d.id} className={`px-2 py-0.5 rounded border cursor-pointer ${on ? "bg-[#171B2E] text-white border-[#171B2E]" : "border-slate-300"}`}>
                              <input type="checkbox" className="hidden" checked={on} onChange={() => { const atts = on ? (e.attachments || []).filter((a) => a.document_ref !== d.id) : [...(e.attachments || []), { filename: d.original_filename, document_ref: d.id }]; editEmail(i, { attachments: atts }); saveEmails(); }} />{d.original_filename}</label>;
                          })}
                        </div>
                      </div>
                    )}
                    <button onClick={() => removeEmail(i)} className="text-xs text-rose-500 hover:underline">Remove email</button>
                  </div>
                ))}
              </div>
            )}

            {stepKey === "engagement" && (
              <div data-testid="hi-step-engagement"><h3 className="font-semibold mb-1">Connect genuine current work</h3>
                <p className="text-[11px] text-slate-400 mb-3">An Active Client must be connected to a real, non-terminal engagement. Select an existing one, or create a genuine current engagement through the normal engagement service (client-facing onboarding stays suppressed until you start it later).</p>
                <div className="flex gap-2 mb-4">
                  {[["select", "Select existing engagement"], ["create", "Create genuine current engagement"]].map(([m, l]) => (
                    <button key={m} onClick={() => setAE({ mode: m })} data-testid={`hi-ae-mode-${m}`}
                      className={`px-3 py-1.5 rounded-lg text-sm border ${ae.mode === m ? "bg-[#171B2E] text-white border-[#171B2E]" : "border-slate-300 text-slate-600"}`}>{l}</button>
                  ))}
                  <button onClick={reclassifyInactive} data-testid="hi-reclassify-inactive" className="ml-auto text-xs text-amber-700 hover:underline">Reclassify as Inactive Client</button>
                </div>

                {ae.mode === "select" && (
                  <div data-testid="hi-ae-select">
                    {draft.mode !== "match" && <p className="text-xs text-rose-500 mb-2">Selecting an existing engagement requires Match mode (choose an existing client in the Match/Create step).</p>}
                    {engList.length === 0 ? <p className="text-xs text-slate-400">No engagements found for this client.</p> : engList.map((e) => (
                      <label key={e.id} className={`block border rounded-lg p-2 mb-2 text-xs cursor-pointer ${ae.engagement_id === e.id ? "border-[#F5A623] bg-amber-50" : "border-slate-200"} ${!e.qualifying ? "opacity-50" : ""}`}>
                        <input type="radio" name="ae-eng" disabled={!e.qualifying} checked={ae.engagement_id === e.id} onChange={() => setAE({ engagement_id: e.id })} className="mr-2 accent-[#F5A623]" data-testid={`hi-ae-eng-${e.id}`} />
                        <span className="font-medium">{e.title || e.engagement_type || e.engagement_number}</span> · {e.tax_year || e.service_period || "—"} · <span className="uppercase">{e.engagement_status}</span>
                        {!e.qualifying && <span className="text-rose-500"> (not qualifying)</span>}
                        <div className="text-slate-400">Services: {(e.services || []).join(", ") || "—"} · team {e.assigned_team_id || "—"} · created {(e.created_at || "").slice(0, 10)} · last activity {(e.last_activity_at || "").slice(0, 10)}</div>
                      </label>
                    ))}
                  </div>
                )}

                {ae.mode === "create" && (
                  <div data-testid="hi-ae-create" className="space-y-2">
                    <div className="grid sm:grid-cols-3 gap-2">
                      <Field label="Engagement type"><input className={inp} value={ae.engagement_type || ""} onChange={(e) => setDraft({ ...draft, active_engagement: { ...ae, engagement_type: e.target.value } })} onBlur={() => setAE({})} data-testid="hi-ae-type" /></Field>
                      <Field label="Tax year / period"><input className={inp} value={ae.tax_year || ""} onChange={(e) => setDraft({ ...draft, active_engagement: { ...ae, tax_year: e.target.value } })} onBlur={() => setAE({})} /></Field>
                      <Field label="Status"><select className={inp} value={ae.status || "generated"} onChange={(e) => setAE({ status: e.target.value })}>{["draft", "generated", "sent", "signed", "active"].map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                      <Field label="Responsible team"><input className={inp} value={ae.responsible_team || ""} onChange={(e) => setDraft({ ...draft, active_engagement: { ...ae, responsible_team: e.target.value } })} onBlur={() => setAE({})} data-testid="hi-ae-team" /></Field>
                      <Field label="Owner / preparer id"><input className={inp} value={ae.owner_id || ""} onChange={(e) => setDraft({ ...draft, active_engagement: { ...ae, owner_id: e.target.value } })} onBlur={() => setAE({})} /></Field>
                      <Field label="Effective date"><input type="date" className={inp} value={(ae.effective_date || "").slice(0, 10)} onChange={(e) => setAE({ effective_date: e.target.value ? e.target.value + "T00:00:00Z" : null })} /></Field>
                    </div>
                    <div>
                      <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wide text-slate-400">Genuine scope of services</span>
                        <button onClick={addSvcLine} data-testid="hi-ae-add-service" className="text-xs text-blue-600">+ Add service</button></div>
                      {(ae.services || []).length === 0 && <p className="text-xs text-slate-400">Add at least one real service (placeholders are rejected).</p>}
                      {(ae.services || []).map((s, i) => (
                        <div key={i} className="grid sm:grid-cols-[2fr_1fr_auto] gap-2 mt-1 items-center">
                          <input className={inp} list="svc-catalog" placeholder="Service name" value={s.name || ""} onChange={(e) => { const hit = svcCatalog.find((x) => x.name === e.target.value); editSvc(i, { name: e.target.value, amount: hit ? hit.price : s.amount, catalog_id: hit ? hit.catalog_id : s.catalog_id }); }} onBlur={() => setAE({})} data-testid={`hi-ae-svc-${i}`} />
                          <input type="number" className={inp} placeholder="Amount" value={s.amount || 0} onChange={(e) => editSvc(i, { amount: parseFloat(e.target.value) || 0 })} onBlur={() => setAE({})} />
                          <button onClick={() => { const services = ae.services.filter((_, idx) => idx !== i); setAE({ services }); }} className="text-xs text-rose-500">×</button>
                        </div>
                      ))}
                      <datalist id="svc-catalog">{svcCatalog.map((s) => <option key={s.catalog_id} value={s.name} />)}</datalist>
                    </div>
                    <Field label="Internal notes (current work)"><textarea className={inp} value={ae.internal_notes || ""} onChange={(e) => setDraft({ ...draft, active_engagement: { ...ae, internal_notes: e.target.value } })} onBlur={() => setAE({})} /></Field>
                    <Field label="Original effective / agreement date (if known)"><input type="date" className={inp} value={(ae.original_effective_date || "").slice(0, 10)} onChange={(e) => setAE({ original_effective_date: e.target.value ? e.target.value + "T00:00:00Z" : null })} /></Field>
                  </div>
                )}
              </div>
            )}

            {stepKey === "notes" && (
              <div><h3 className="font-semibold mb-3">Historical notes</h3>
                <button onClick={() => setDraft({ ...draft, notes: [...(draft.notes || []), { title: "", body: "", note_type: "historical", note_date: "" }] })} className="text-xs text-blue-600 mb-2">+ Add note</button>
                {(draft.notes || []).map((n, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-2 mb-2 space-y-1">
                    <input className={inp} placeholder="Title" value={n.title} onChange={(e) => { const notes = [...draft.notes]; notes[i] = { ...n, title: e.target.value }; setDraft({ ...draft, notes }); }} onBlur={() => save({ notes: draft.notes })} />
                    <textarea className={inp} placeholder="Body" value={n.body} onChange={(e) => { const notes = [...draft.notes]; notes[i] = { ...n, body: e.target.value }; setDraft({ ...draft, notes }); }} onBlur={() => save({ notes: draft.notes })} />
                  </div>
                ))}
                {(draft.notes || []).length === 0 && <p className="text-xs text-slate-400">No historical notes added.</p>}
              </div>
            )}

            {stepKey === "review" && (
              <div data-testid="hi-review"><h3 className="font-semibold mb-3">Review & finalize</h3>
                {activeBlocked && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 mb-3" data-testid="hi-active-warning">Active Client import requires connecting genuine current work and will be available in Phase 3. Reclassify as Inactive Client, or save as draft.</div>}

                {/* Lifecycle consequence block — classification-aware */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 text-[11px]" data-testid="hi-lifecycle-consequence">
                  <p className="font-semibold text-slate-700 mb-1.5">Import result</p>
                  <ul className="space-y-0.5 text-slate-600">
                    <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> Existing historical client record created</li>
                    {(review?.services?.total ?? (draft?.historical_services || []).length) > 0 && (
                      <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> {review?.services?.total ?? (draft?.historical_services || []).length} historical service(s) recorded as completed and paid</li>
                    )}
                    {classification === "active_client" ? (
                      <li className="flex items-center gap-1.5"><Icons.Info className="h-3.5 w-3.5 text-blue-400 shrink-0" /> Current engagement will be created — client-facing onboarding suppressed until explicitly started</li>
                    ) : (
                      <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> No active workflow will be created</li>
                    )}
                    <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> No invoice will be generated</li>
                    <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> No client communication will be sent</li>
                    <li className="flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> No portal invitation will be sent</li>
                  </ul>
                </div>

                {review?.warnings?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="hi-review-warnings">
                    <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1"><Icons.AlertTriangle className="h-3.5 w-3.5" /> Validation warnings</p>
                    <ul className="text-[11px] text-amber-700 list-disc pl-4">{review.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </div>
                )}

                {review?.tax_profile?.unresolved?.length > 0 && (
                  <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 mb-3" data-testid="hi-tp-conflicts">
                    <p className="text-xs font-semibold text-rose-700 mb-2">Tax profile conflicts — choose an action for each</p>
                    {review.tax_profile.conflicts.map((c) => (
                      <div key={c.field_key} className="mb-2 text-xs">
                        <div className="font-medium">{c.label}</div>
                        <div className="text-slate-500">Existing: <b>{String(c.existing_value)}</b> <span className="text-slate-400">({c.existing_source}/{c.existing_status})</span> → Imported: <b>{String(c.imported_value)}</b></div>
                        <div className="flex gap-3 mt-1">
                          {["keep", "replace", "skip"].map((dz) => (
                            <label key={dz} className="flex items-center gap-1"><input type="radio" name={`dec-${c.field_key}`} checked={c.decision === dz} onChange={() => setConflictDecision(c.field_key, dz)} data-testid={`hi-tp-decision-${c.field_key}-${dz}`} className="accent-[#F5A623]" />{dz === "keep" ? "Keep existing" : dz === "replace" ? "Replace with imported" : "Skip imported"}</label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <dl className="text-sm grid sm:grid-cols-2 gap-y-1">
                  <dt className="text-slate-400 text-xs">Classification</dt><dd>{classification?.replace("_", " ")}</dd>
                  <dt className="text-slate-400 text-xs">Mode</dt><dd>{draft.mode || "create"}</dd>
                  <dt className="text-slate-400 text-xs">Name</dt><dd>{[draft.contact?.first_name, draft.contact?.last_name].filter(Boolean).join(" ") || draft.contact?.business_name || "—"}</dd>
                  <dt className="text-slate-400 text-xs">Email</dt><dd>{draft.contact?.email || "—"}</dd>
                  {review?.rich && <><dt className="text-slate-400 text-xs">Tax profile facts to add</dt><dd data-testid="hi-rv-tp-add">{review.tax_profile.facts_to_add} (existing preserved: {review.tax_profile.existing_preserved}, will change: {review.tax_profile.will_change})</dd></>}
                  {review?.rich && <><dt className="text-slate-400 text-xs">Documents</dt><dd data-testid="hi-rv-docs">{review.documents.total} · years {Object.keys(review.documents.by_tax_year).join(", ") || "—"}{review.documents.unclassified ? ` · ${review.documents.unclassified} unclassified` : ""}</dd></>}
                  <dt className="text-slate-400 text-xs">Email threads / messages</dt><dd data-testid="hi-rv-emails">{review ? `${review.emails.threads} thread(s), ${review.emails.messages} message(s), ${review.emails.attachments} attachment(s)` : "—"}</dd>
                  <dt className="text-slate-400 text-xs">Notes</dt><dd>{(draft.notes || []).length}</dd>
                </dl>

                {/* Historical Services summary in review */}
                {(review?.services?.total ?? (draft?.historical_services || []).length) > 0 && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="hi-rv-services">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Historical services ({review?.services?.total ?? (draft?.historical_services || []).length})</p>
                    <div className="space-y-1">
                      {(review?.services?.entries ?? draft?.historical_services ?? []).map((s, i) => (
                        <div key={i} className="text-xs flex items-baseline justify-between gap-2">
                          <span className="font-medium truncate">{s.service_name}{s.service_code ? ` (${s.service_code})` : ""}</span>
                          <span className="text-slate-400 shrink-0">{s.tax_year || s.service_period || "—"}</span>
                          {s.completed_date && <span className="text-slate-400 shrink-0">Completed {s.completed_date?.slice(0, 10)}</span>}
                          <span className="font-medium shrink-0 text-emerald-700">{s.currency || "USD"} {Number(s.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} paid</span>
                          {s.amount_charged && <span className="text-slate-400 shrink-0">({s.currency || "USD"} {Number(s.amount_charged).toLocaleString(undefined, { minimumFractionDigits: 2 })} charged)</span>}
                          <span className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded shrink-0">Completed · Paid</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-[11px] text-slate-500">Finalize will create the record in TaxFlow. <b>No</b> client emails, portal invitations, engagement letters, invoices, organizers, reminders, workflow automations, or AI runs are triggered.</div>
                <button disabled={busy || activeBlocked || (review?.tax_profile?.unresolved?.length > 0)} onClick={() => finalize(false)} data-testid="hi-finalize" className="mt-4 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2">{busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}Finalize Import</button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4">
            <button onClick={exitWizard} className="text-xs text-slate-500 hover:underline" data-testid="hi-save-draft">Save draft & exit</button>
            <div className="flex gap-2">
              <button disabled={stepIdx === 0} onClick={() => setStepIdx((s) => s - 1)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg disabled:opacity-40" data-testid="hi-back">Back</button>
              {stepIdx < steps.length - 1 && <button onClick={() => setStepIdx((s) => s + 1)} className="px-3 py-1.5 text-sm bg-[#171B2E] text-white rounded-lg" data-testid="hi-next">Next</button>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
